import { useEffect, useState } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL
  || process.env.REACT_APP_API_URL
  || process.env.REACT_APP_API_BASE
  || (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');
const API_BASE = getBackendUrl();

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function Message({ title, children, error }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7fb', display: 'grid', placeItems: 'center', px: 2 }}>
      <Paper elevation={0} sx={{ maxWidth: 480, p: 4, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>{title}</Typography>
        <Typography color={error ? 'error' : 'text.secondary'}>{children}</Typography>
      </Paper>
    </Box>
  );
}

// Public, read-only payment-receipt page — opened from a short link (e.g.
// strz.me/ufvhiuag) sent to a patient via SMS/email after a mobile POS
// payment. Fetches /api/receipts/:token, which s4forms reverse-proxies
// straight through to s4-s3 (see server/index.js's proxyToS4S3); the token
// itself is the only credential, same model as the estimate/postop-contact
// share links.
export default function ReceiptSharePage({ token }) {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid link.');
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/api/receipts/${encodeURIComponent(token)}`, { cache: 'no-store' })
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'Unable to load this receipt.');
        setReceipt(payload.receipt);
      })
      .catch((err) => setError(err.message || 'Unable to load this receipt.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!receipt) return <Message title="Unable to Open Receipt" error={error}>{error || 'This receipt link is unavailable.'}</Message>;

  const items = Array.isArray(receipt.items) && receipt.items.length
    ? receipt.items
    : [{ label: receipt.paymentReason || 'Patient Payment', amount: receipt.amount != null ? receipt.amount.toFixed(2) : null }];
  const cardLine = receipt.cardBrand || receipt.cardLast4
    ? `${receipt.cardBrand || 'Card'}${receipt.cardLast4 ? ` ending in ${receipt.cardLast4}` : ''}`
    : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7fb', px: 2, py: { xs: 3, sm: 6 } }}>
      <Paper elevation={0} sx={{ maxWidth: 480, mx: 'auto', borderRadius: 3, overflow: 'hidden', border: '1px solid #dbe5f1' }}>
        <Box sx={{ bgcolor: '#1565c0', color: '#fff', px: 3, py: 2.5 }}>
          <Typography variant="overline" sx={{ opacity: 0.85 }}>{receipt.practiceName}</Typography>
          <Typography variant="h5" fontWeight={700}>Payment Receipt</Typography>
        </Box>
        <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Typography color="text.secondary">{receipt.patientName}</Typography>
          <Typography color="text.secondary" variant="body2">{formatDate(receipt.paymentDate)}</Typography>
          {cardLine && <Typography color="text.secondary" variant="body2">{cardLine}</Typography>}

          <Box sx={{ mt: 2.5 }}>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 1,
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Typography fontWeight={600}>{item.label}</Typography>
                {item.amount && <Typography fontWeight={700} whiteSpace="nowrap">${item.amount}</Typography>}
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, mt: 0.5, borderTop: '2px solid #172033' }}>
              <Typography fontWeight={800} fontSize={17}>Total</Typography>
              <Typography fontWeight={800} fontSize={17} color="primary">${receipt.amount != null ? receipt.amount.toFixed(2) : ''}</Typography>
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
            Receipt #{receipt.depositId} &middot; Thank you for your payment.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
